// PR-16.2 — pure helpers for the player-facing league card. Kept in
// lib/ so vitest can import without the supabase client.
//
// Used by `<PlayerLeagueCard>` (Leagues.tsx + LeagueList.tsx) to:
//   - Derive the weekdays of the active round from scheduled matches
//     (Q2 spec: "Mina serier (active round) reads from actually
//     scheduled matches, not admin's checkboxes")
//   - Format weekday lists into short Swedish/English labels
//   - Produce safe `target="_blank" rel="noopener noreferrer"` props
//     for the sponsor click-through link
//   - Format dates with locale-aware fallbacks

const WEEKDAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

const SHORT_LABELS_SV: Record<WeekdayKey, string> = {
  monday: "Mån",
  tuesday: "Tis",
  wednesday: "Ons",
  thursday: "Tor",
  friday: "Fre",
  saturday: "Lör",
  sunday: "Sön",
};

const SHORT_LABELS_EN: Record<WeekdayKey, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

/**
 * Returns the distinct weekday keys (e.g. ["monday", "wednesday"]) for
 * matches in the given round. Caller is responsible for picking the
 * "active" round — see PR-10.2's computeRoundStatus() helper.
 *
 * Returns the empty array if `activeRoundNumber` is null or no matches
 * are scheduled in that round (caller should fall back to
 * `league.play_days` in that case — admin's checkbox config).
 *
 * Order: weekdays appear in canonical Mon..Sun order regardless of the
 * order in which matches were scheduled.
 */
export function deriveActiveRoundWeekdays(
  matches: { round: number; scheduled_date: string | null }[],
  activeRoundNumber: number | null,
): string[] {
  if (activeRoundNumber == null) return [];
  const seen = new Set<WeekdayKey>();
  for (const m of matches) {
    if (m.round !== activeRoundNumber) continue;
    if (!m.scheduled_date) continue;
    // scheduled_date is YYYY-MM-DD; UTC interpretation is fine for
    // weekday-only derivation since we don't care about TZ-shift edge
    // cases for round display.
    const d = new Date(m.scheduled_date + "T00:00:00Z");
    const dow = d.getUTCDay(); // 0 = Sunday … 6 = Saturday
    seen.add(WEEKDAY_KEYS[dow]);
  }
  // Render in canonical order (Mon-first; Sun last)
  const ORDER: WeekdayKey[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  return ORDER.filter((k) => seen.has(k));
}

/**
 * Converts a list of weekday keys (e.g. ["monday", "wednesday"]) to
 * short locale-specific labels (e.g. ["Mån", "Ons"]). Unknown keys
 * are silently dropped.
 */
export function weekdaysToShortLabels(
  days: string[],
  lang: "sv" | "en" = "sv",
): string[] {
  const dict = lang === "en" ? SHORT_LABELS_EN : SHORT_LABELS_SV;
  const out: string[] = [];
  for (const d of days) {
    const label = dict[d as WeekdayKey];
    if (label) out.push(label);
  }
  return out;
}

/**
 * Returns the props for a safe external link wrapping a sponsor logo.
 * Returns null when no click URL is provided (caller renders the logo
 * as a plain image without anchor wrapping).
 *
 * Adds `noopener` and `noreferrer` to mitigate window.opener tampering
 * + referer leak. `target="_blank"` matches the spec's "click sponsor
 * → opens URL in new tab".
 */
export function safeSponsorClickProps(
  clickUrl: string | null | undefined,
): { href: string; target: "_blank"; rel: "noopener noreferrer" } | null {
  if (!clickUrl) return null;
  const trimmed = clickUrl.trim();
  if (!trimmed) return null;
  // Reject relative-only URLs without scheme — prevents accidental
  // navigation within the app when the admin pasted a path.
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return { href: trimmed, target: "_blank", rel: "noopener noreferrer" };
}

/**
 * Formats a date column (YYYY-MM-DD or ISO string) into a short
 * locale-aware label like "30 apr" / "30 Apr". Returns null when the
 * input is missing or invalid (caller decides how to handle — either
 * skip the row or show a placeholder).
 *
 * Intl.DateTimeFormat is used directly instead of date-fns to avoid
 * a heavy import chain in this small helper. The 2-letter month
 * abbreviation matches the existing `format(d, "d MMM", { locale: sv })`
 * convention used elsewhere in the player flow.
 */
export function formatLeagueDate(
  iso: string | null | undefined,
  lang: "sv" | "en" = "sv",
): string | null {
  if (!iso) return null;
  // Accept "YYYY-MM-DD" or full ISO. Date.parse handles both via Date.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "sv-SE", {
    day: "numeric",
    month: "short",
  });
  return fmt.format(d);
}
